import { Box, BoxProps, Button } from '@mui/material';
import PublicQuantity from 'components/PublicQuantity';
import SafeImage from 'components/SafeImage';
import { Link } from 'components/common/Link';
import { useCart } from 'hooks/useCart';
import { useProduct } from 'hooks/useProduct';

import { useState } from 'react';
import useUser from 'stores/useCustomer';
import { colors } from 'styles/theme/colors';
import { useServerTranslation } from 'hooks/useServerTranslation';
import { Product as ProductType } from '../../generated/graphql';
import { Favorite } from '../Favourite';
import { ShowHide } from '../common/ShowHide';
import { AddToCartButton } from './components/AddToCartButton';
import { ProductListVariants } from './components/ProductListVariants';
import ProductPrice from './components/ProductPrice';
import { Title } from './components/Title';
import { useNavigation } from '../../hooks/useNavigation';

type ProductProps = {
    product: ProductType;
    boxProps?: BoxProps;
};

export default function Product({ product, boxProps }: ProductProps) {
    const { t } = useServerTranslation();
    const isLoggedIn = useUser((state) => state.useLoggedIn());
    const { addToCart } = useCart();
    const {
        isWrapperProductSelected: isWrapper,
        isSimpleProductSelected: isSimple,
        isAddedToCart,
        quantityInCart,
    } = useProduct(product);
    const [selectedQuantity, setSelectedQuantity] = useState(quantityInCart ?? 1);

    const { shop } = useNavigation();

    if (!product) {
        return null;
    }

    const {
        images,
        name,
    } = product;

    const onAddToCartHandler = () => {
        addToCart({
            productId: product.id,
            quantity: selectedQuantity,
        });
    };

    const [image] = images ?? [];

    const productPageHref = shop.product(product.slug || '');

    return (
        <Box
            position="relative"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            height="100%"
            {...boxProps}
        >
            <Box sx={{ img: { width: 'auto', transition: 'transform 0.3s' }, 'a:hover img': { transform: 'scale(1.05)' } }}>
                <Link href={productPageHref} style={{ display: 'block', marginBottom: '5px', overflow: 'hidden' }}>
                    <SafeImage
                        priority
                        alt="product image"
                        src={image?.full ?? '/'}
                        width={190}
                        height={190}
                        sizes="(max-width: 600px) 100vw, (max-width: 1199px) 33vw, 211px"
                        style={{ height: 'auto', width: '100%', display: 'block ' }}
                    />
                </Link>
                <ShowHide show={isLoggedIn} fallback={<div />}>
                    <Favorite
                        iconProps={{ sx: { position: 'absolute', right: 0, top: 0, zIndex: 10 } }}
                        productId={product.id}
                    />
                </ShowHide>
                <Title>{name}</Title>
            </Box>
            <Box display="grid">
                <Box sx={{
                    flex: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'nowrap',
                    gap: '10px',
                    paddingBottom: '10px',
                }}
                >
                    <ShowHide show={isSimple} fallback={<div />}>
                        <ProductPrice product={product} />
                    </ShowHide>
                    {isSimple && (
                        <Box sx={{ justifyContent: 'end', display: 'flex', alignSelf: 'flex-end' }}>
                            <PublicQuantity
                                value={selectedQuantity}
                                onChange={(value) => {
                                    setSelectedQuantity(value);
                                }}
                            />
                        </Box>
                    )}
                </Box>
                {isWrapper && (
                    <Box paddingBottom={2}>
                        <ProductListVariants product={product} />
                    </Box>
                )}
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: ['flex-end'],
                    paddingBottom: { xs: '10px', sm: undefined },
                }}
                >
                    <Box flexGrow={1}>
                        {
                            isWrapper ? (
                                <Link
                                    href={productPageHref}
                                >
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        sx={{ display: 'flex', color: colors.pink2 }}
                                    >
                                        {t('product.view-wrapper')}
                                    </Button>
                                </Link>
                            ) : (
                                <AddToCartButton
                                    onClick={onAddToCartHandler}
                                    isAddedToCart={isAddedToCart}
                                />
                            )
                        }
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
